import { useState } from 'react'
import { Plus, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { ConceptChip } from './ConceptBadges'
import { ConceptPicker } from './ConceptPicker'
import { ProposalCard } from './ProposalCard'
import { RELATION_INVERSE_LABEL, RELATION_LABEL } from './ruleText'
import { RELATION_TYPES, type Concept, type ConceptRelation, type Proposal, type RelationType } from '../api/knowledgeApi'
import { useRelationEdit } from '../hooks/useKnowledge'

type Picked = Pick<Concept, 'ucid' | 'name' | 'category' | 'artworkCount'>

/** Typed relations around a concept: broader / narrower / variants / opposites / related. */
export function ConceptRelations({ concept, relations }: { concept: Concept; relations: ConceptRelation[] }) {
  const edit = useRelationEdit()
  const [adding, setAdding] = useState(false)
  const [type, setType] = useState<RelationType>('related_to')
  const [target, setTarget] = useState<Picked | null>(null)

  const groups = new Map<string, ConceptRelation[]>()
  for (const r of relations) {
    const label = r.direction === 'out' ? RELATION_LABEL[r.relationType] : RELATION_INVERSE_LABEL[r.relationType]
    groups.set(label, (groups.get(label) ?? []).concat(r))
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm">Relations</CardTitle>
        <CardDescription>
          What similarity cannot say: broader and narrower concepts, variants, opposites. Accepted from
          model proposals or added here.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {relations.length === 0 && !adding && <p className="text-sm text-slate-500">None yet.</p>}
        {[...groups.entries()].map(([label, rs]) => (
          <div key={label}>
            <div className="mb-1 text-xs text-slate-500">This is {label}</div>
            <div className="flex flex-wrap gap-1.5">
              {rs.map((r) => (
                <span key={`${r.sourceUcid}:${r.targetUcid}:${r.relationType}`} className="inline-flex items-center gap-1">
                  <ConceptChip concept={r} />
                  <button
                    className="text-slate-300 hover:text-rose-600"
                    title="Remove relation"
                    onClick={() => edit.mutate({ op: 'remove', source: r.sourceUcid, target: r.targetUcid, type: r.relationType })}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </span>
              ))}
            </div>
          </div>
        ))}
        {adding ? (
          <div className="space-y-2 rounded border p-2">
            <Select value={type} onValueChange={(v) => setType(v as RelationType)}>
              <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
              <SelectContent>
                {RELATION_TYPES.map((r) => <SelectItem key={r} value={r}>This is {RELATION_LABEL[r]}…</SelectItem>)}
              </SelectContent>
            </Select>
            <ConceptPicker value={target} onChange={setTarget} exclude={[concept.ucid]} />
            <div className="flex gap-2">
              <Button
                size="sm"
                disabled={!target || edit.isPending}
                onClick={() =>
                  target && edit.mutate(
                    { op: 'add', source: concept.ucid, target: target.ucid, type },
                    { onSuccess: () => { setAdding(false); setTarget(null) } }
                  )
                }
              >
                Add
              </Button>
              <Button size="sm" variant="outline" onClick={() => setAdding(false)}>Cancel</Button>
            </div>
          </div>
        ) : (
          <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
            <Plus className="mr-1.5 h-3.5 w-3.5" /> Add relation
          </Button>
        )}
      </CardContent>
    </Card>
  )
}

/** Pending model proposals that involve this concept. */
export function ConceptProposals({ proposals }: { proposals: Proposal[] }) {
  if (!proposals.length) return null
  return (
    <Card className="border-violet-200">
      <CardHeader className="pb-1">
        <CardTitle className="text-sm">Model proposals ({proposals.length})</CardTitle>
        <CardDescription>Suggested by the nightly resolution run. Nothing changes until you accept.</CardDescription>
      </CardHeader>
      <CardContent>
        {proposals.map((p) => <ProposalCard key={p.proposalId} p={p} />)}
      </CardContent>
    </Card>
  )
}
